import { isEmpty } from 'ramda';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';

import { ServiceType } from '@/shared/api/protocol-ts/api/billing_new/dto_services_new_pb';
import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { StudyType } from '@/shared/api/protocol-ts/model/dto_study_pb';
import { UploadSessionStatus } from '@/shared/api/protocol-ts/model/dto_upload_session_pb';
import {
  FEATURE_FLAG_ENABLE_SCRIBE_FEATURE,
  PATIENT_PROFILE_SCROLL_KEY_PREFIX,
} from '@/shared/config';
import {
  useAppDispatch,
  useAppSelector,
  useIsNonDentalOrganization,
  useMedia,
  useScrollRestoration,
} from '@/shared/hooks';
import { scrollToElementByID, trackEvent } from '@/shared/lib';
import {
  Button,
  Column,
  Layout,
  MobileLayout,
  WidgetCard,
  WidgetLayout,
} from '@/shared/ui';

import { albumModel } from '@/entities/album';
import { billingModel, usePurchasePrompt } from '@/entities/billing';
import { composedModel } from '@/entities/composed';
import { ConfirmModal, ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import { patientModel } from '@/entities/patient';
import { studyModel } from '@/entities/study';
import { userModel } from '@/entities/user';

import { Footer } from '@/features/footer';
import { orderReportSelectors } from '@/features/orderReport';
import {
  reportCardsModel,
  type StructuredReportsAndStudiesType,
} from '@/features/reportCards';
import {
  MobileDentalPhotoUploadMenu,
  UploadDentalPhotosModal,
} from '@/features/uploadDentalPhotos';

import { AboutLabelingModal } from '@/widgets/AboutLabelingModal';
import { AgentChatHost } from '@/widgets/AgentChat';
import { CongratsModal } from '@/widgets/CongratsModal';
import { DentalPhotoReportCard } from '@/widgets/DentalPhotoReportCard';
import { GetFullAccessModal } from '@/widgets/GetFullAccessModal';
import { Header, MobileHeader } from '@/widgets/Header';
import { MobileOrderReportModal } from '@/widgets/MobileOrderReportModal';
import { OrderReport } from '@/widgets/OrderReport';
import { OrderReportModal } from '@/widgets/OrderReportModal';
import { PatientDocuments } from '@/widgets/PatientDocuments';
import { PatientInfo } from '@/widgets/PatientInfo';
import { PatientModalForm } from '@/widgets/PatientModalForm';
import { PatientRecordingsCard } from '@/widgets/PatientRecordingsCard';
import { PurchasePrompt } from '@/widgets/PurchasePrompt';
import { RawMeshViewerCard } from '@/widgets/RawMeshViewerCard';
import { RawMeshViewerInfoModal } from '@/widgets/RawMeshViewerInfoModal';
import { ReportCard } from '@/widgets/ReportCard';
import { ReportInfoModal } from '@/widgets/ReportInfoModal';

import { UnifiedItem } from './config';
import { getUnifiedItems, splitIntoColumns } from './lib';
import styles from './PatientProfile.module.scss';
import { PatientPersonalInfo } from './ui/PatientPersonalInfo/PatientPersonalInfo';
import { ReportCardSkeletons } from './ui/ReportCardSkeletons/ReportCardSkeletons';

const DENTAL_PHOTO_WIDGET_ID = 'patient-profile-dental-photo-widget';
const DENTAL_PHOTO_WIDGET_SCROLL_KEY = 'dental-photo-widget-scroll-target';
const NO_HIDDEN_REPORTS: StructuredReportsAndStudiesType[] = [];

export const PatientProfile: FC = () => {
  const { patientID = '' } = useParams();
  const [agentDocumentsRefreshToken, setAgentDocumentsRefreshToken] =
    useState(0);
  const [showHidden, setShowHidden] = useState(false);

  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();
  const isNonDental = useIsNonDentalOrganization();

  const user = useAppSelector(userModel.selectors.selectCurrentUser);

  const patient = useAppSelector(
    patientModel.selectors.selectPatientByID(patientID),
  );

  const { CanEditPatient, CanViewStudies, CanRequestReport } = useAppSelector(
    patientModel.selectors.selectPatientYourPermissions(patientID),
  );

  const hasActiveSubscriptionOrPackages = useAppSelector(
    billingModel.selectors.selectHasActiveSubscriptionOrPackages,
  );

  const hasAnyVisibleOrderReportButton = useAppSelector(
    orderReportSelectors.selectHasAnyVisibleOrderReportButton,
  );

  const {
    shouldShow: shouldShowPurchasePrompt,
    isOwner,
    openShop,
  } = usePurchasePrompt();

  const enablePhotosFeature = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Show_DentalPhotosWidget,
    ),
  );

  const isUploadPhotoServiceActive = useAppSelector(
    billingModel.selectors.selectIsServiceActiveByType(
      ServiceType.ServiceType_Enable_UploadDentalPhoto,
    ),
  );

  const hasDentalPhotos = useAppSelector(albumModel.selectors.hasDentalPhotos);
  const dentalPhotoCount = useAppSelector(
    (state) => albumModel.selectors.selectAllPhotos(state).length,
  );

  const shouldShowPhotosWidget =
    !isNonDental &&
    (enablePhotosFeature || isUploadPhotoServiceActive || hasDentalPhotos);

  const enableScribeFeature = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FEATURE_FLAG_ENABLE_SCRIBE_FEATURE,
    ),
  );

  const enableAgentChatFeature = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_AgentChat,
    ),
  );

  const canViewArchive = useAppSelector(
    composedModel.selectors.selectCanViewPatientReportsArchive(patientID),
  );

  const reportCardsLoading = useAppSelector(
    reportCardsModel.selectors.selectLoading,
  );

  const canUploadDentalPhoto =
    enablePhotosFeature || isUploadPhotoServiceActive;

  const showOrderReport =
    CanRequestReport &&
    (hasActiveSubscriptionOrPackages ||
      enableScribeFeature ||
      canUploadDentalPhoto) &&
    hasAnyVisibleOrderReportButton;

  const { sortedStructuredReportsAndStudies } = useAppSelector(
    reportCardsModel.selectors.selectStructuredReportsAndStudiesByPatientID(
      patientID,
    ),
  );

  // The hidden view is only built while the flag is on: with it off, archived
  // reports arriving through the live profile stream must not surface here.
  const hiddenStructuredReportsAndStudies = useAppSelector((state) =>
    canViewArchive
      ? reportCardsModel.selectors.selectHiddenStructuredReportsAndStudiesByPatientID(
          patientID,
        )(state).sortedStructuredReportsAndStudies
      : NO_HIDDEN_REPORTS,
  );

  const rawMeshViewerStudies = useAppSelector(
    studyModel.selectors.selectNotDeletedStudiesByPatientID(patientID),
  );

  const showSkeletons =
    reportCardsLoading === 'idle' || reportCardsLoading === 'pending';

  const rawMeshViewerItems = useMemo(() => {
    return rawMeshViewerStudies
      .filter(
        (study) =>
          study.Type === StudyType.StudyType_IOS_Meshes &&
          study.RawMeshViewer &&
          study.Status === UploadSessionStatus.UploadSessionStatusSuccessful,
      )
      .map((study) => ({
        type: 'rawMeshViewer' as const,
        data: study,
        date: study.Created?.At?.toDate().getTime() ?? 0,
      }));
  }, [rawMeshViewerStudies]);

  const { combinedLeftColumn, combinedRightColumn, allItems } = useMemo(() => {
    const allItems = getUnifiedItems({
      reports: sortedStructuredReportsAndStudies,
      rawMeshViewerItems,
    });
    const [combinedLeftColumn, combinedRightColumn] =
      splitIntoColumns(allItems);

    return { combinedLeftColumn, combinedRightColumn, allItems };
  }, [sortedStructuredReportsAndStudies, rawMeshViewerItems]);

  const { hiddenLeftColumn, hiddenRightColumn, hiddenItems } = useMemo(() => {
    const hiddenItems = getUnifiedItems({
      reports: hiddenStructuredReportsAndStudies,
    });
    const [hiddenLeftColumn, hiddenRightColumn] = splitIntoColumns(hiddenItems);

    return { hiddenLeftColumn, hiddenRightColumn, hiddenItems };
  }, [hiddenStructuredReportsAndStudies]);

  const renderUnifiedItem = (item: UnifiedItem) =>
    item.type === 'report' ? (
      <ReportCard key={item.data.ID} structuredReportsAndStudies={item.data} />
    ) : (
      <RawMeshViewerCard key={item.data.ID} study={item.data} />
    );

  const showHiddenSection = canViewArchive && hiddenItems.length > 0;
  const isHiddenSectionExpanded = showHiddenSection && showHidden;

  const hiddenToggle = showHiddenSection && (
    <Button
      variant="tertiary"
      icon={showHidden ? 'eye' : 'eyeClosed'}
      onClick={() => setShowHidden((current) => !current)}
      className={styles.showHiddenButton}
    >
      {showHidden
        ? formatMessage({
            id: 'patientProfile.hideHiddenReports',
            defaultMessage: 'Collapse archived reports',
          })
        : formatMessage(
            {
              id: 'patientProfile.showHiddenReports',
              defaultMessage: 'Show archived reports ({count, number})',
            },
            { count: hiddenItems.length },
          )}
    </Button>
  );

  useScrollRestoration({
    storageKey: `${PATIENT_PROFILE_SCROLL_KEY_PREFIX}${patientID}`,
    isContentLoaded: !showSkeletons,
    maxAttempts: 10,
    attemptInterval: 200,
    timeoutMinutes: 5,
  });

  useEffect(() => {
    trackEvent({
      eventName: 'PatientProfile Page Viewed',
      properties: { patient_id: patientID },
      isPageViewEvent: true,
    });
  }, [patientID]);

  useEffect(() => {
    const pendingScrollPatientID = sessionStorage.getItem(
      DENTAL_PHOTO_WIDGET_SCROLL_KEY,
    );

    if (!shouldShowPhotosWidget || pendingScrollPatientID !== patientID) {
      return;
    }

    sessionStorage.removeItem(DENTAL_PHOTO_WIDGET_SCROLL_KEY);
    setTimeout(() => {
      scrollToElementByID(DENTAL_PHOTO_WIDGET_ID, 'smooth', 'start');
    }, 0);
  }, [patientID, shouldShowPhotosWidget, dentalPhotoCount]);

  if (isEmpty(user) || patient === undefined) {
    return null;
  }

  const handleEditPatient = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.PatientModalForm,
        data: {
          patientID,
        },
      }),
    );
  };

  const openMobileOrderReportModal = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.MobileOrderReportModal,
      }),
    );
  };

  return (
    <>
      {isMobile ? (
        <MobileLayout>
          <MobileHeader />

          <MobileLayout.Main>
            <PatientPersonalInfo
              canEditPatient={CanEditPatient}
              onEditPatient={handleEditPatient}
            />

            <PatientInfo patientID={patientID} />

            {enableAgentChatFeature && (
              <PatientDocuments
                key={patientID}
                patientID={patientID}
                canEdit={CanEditPatient}
                refreshToken={agentDocumentsRefreshToken}
              />
            )}

            {CanViewStudies && <PatientRecordingsCard patientID={patientID} />}

            {shouldShowPurchasePrompt && (
              <PurchasePrompt
                title={formatMessage({
                  id: 'orderReport.title',
                  defaultMessage: 'Create AI report',
                })}
                isOwner={isOwner}
                onOpenShop={openShop}
              />
            )}

            {CanViewStudies && showSkeletons && <ReportCardSkeletons />}

            {CanViewStudies && allItems.map(renderUnifiedItem)}

            {shouldShowPhotosWidget && (
              <DentalPhotoReportCard
                patientID={patientID}
                elementID={DENTAL_PHOTO_WIDGET_ID}
              />
            )}

            {hiddenToggle}

            {isHiddenSectionExpanded && hiddenItems.map(renderUnifiedItem)}
          </MobileLayout.Main>

          {showOrderReport && (
            <MobileLayout.Footer>
              <Button
                data-component="create-ai-report-button"
                className={styles.mobileOrderReportButton}
                onClick={openMobileOrderReportModal}
                icon="plus"
              >
                {formatMessage({
                  id: 'mobileOrderReportModal.createReport',
                  defaultMessage: 'Create AI report',
                })}
              </Button>
            </MobileLayout.Footer>
          )}
        </MobileLayout>
      ) : (
        <Layout>
          <Layout.Header>
            <Header />
          </Layout.Header>

          <Layout.Content className={styles.desktopContent}>
            <div className={styles.desktopWorkspace}>
              <Layout.Main className={styles.main}>
                <div className={styles.header} data-section="patient-profile">
                  <PatientPersonalInfo
                    canEditPatient={CanEditPatient}
                    onEditPatient={handleEditPatient}
                  />
                </div>

                <WidgetLayout>
                  <Column className={styles.column}>
                    <PatientInfo patientID={patientID} />

                    {enableAgentChatFeature && (
                      <PatientDocuments
                        key={patientID}
                        patientID={patientID}
                        canEdit={CanEditPatient}
                        refreshToken={agentDocumentsRefreshToken}
                      />
                    )}

                    {CanViewStudies && (
                      <PatientRecordingsCard patientID={patientID} />
                    )}

                    {CanViewStudies && showSkeletons && <ReportCardSkeletons />}

                    {CanViewStudies &&
                      combinedLeftColumn.map(renderUnifiedItem)}
                  </Column>

                  <Column className={styles.column}>
                    {shouldShowPurchasePrompt && (
                      <PurchasePrompt
                        title={formatMessage({
                          id: 'orderReport.title',
                          defaultMessage: 'Create AI report',
                        })}
                        isOwner={isOwner}
                        onOpenShop={openShop}
                      />
                    )}

                    {showOrderReport && (
                      <WidgetCard>
                        <OrderReport />
                      </WidgetCard>
                    )}

                    {CanViewStudies &&
                      combinedRightColumn.map(renderUnifiedItem)}

                    {shouldShowPhotosWidget && (
                      <DentalPhotoReportCard
                        patientID={patientID}
                        elementID={DENTAL_PHOTO_WIDGET_ID}
                      />
                    )}
                  </Column>
                </WidgetLayout>

                {showHiddenSection && (
                  <div className={styles.showHiddenSection}>
                    {hiddenToggle}

                    {isHiddenSectionExpanded && (
                      <WidgetLayout>
                        <Column className={styles.column}>
                          {hiddenLeftColumn.map(renderUnifiedItem)}
                        </Column>

                        <Column className={styles.column}>
                          {hiddenRightColumn.map(renderUnifiedItem)}
                        </Column>
                      </WidgetLayout>
                    )}
                  </div>
                )}
              </Layout.Main>
            </div>
          </Layout.Content>

          <Layout.Footer>
            <Footer />
          </Layout.Footer>
        </Layout>
      )}

      {enableAgentChatFeature && (
        <AgentChatHost
          patientID={patientID}
          onDocumentChanged={() =>
            setAgentDocumentsRefreshToken((current) => current + 1)
          }
        />
      )}

      <PatientModalForm />
      <OrderReportModal />
      <MobileOrderReportModal />
      <MobileDentalPhotoUploadMenu />
      {patientID && <UploadDentalPhotosModal patientID={patientID} />}
      <ConfirmModal />
      <ReportInfoModal />
      <RawMeshViewerInfoModal />
      <AboutLabelingModal />
      <GetFullAccessModal />
      <CongratsModal />
    </>
  );
};
